import { useEffect, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { intelliTrackClient } from '../lib/supabase'
import { UserProfile } from '../types/database'

export function useProfile() {
  const { user } = useAuth()
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!user) {
      setProfile(null)
      setLoading(false)
      return
    }

    const fetchProfile = async () => {
      try {
        setLoading(true)
        const { data, error } = await intelliTrackClient
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single()

        if (error) {
          // If profile doesn't exist, create it
          if (error.code === 'PGRST116') {
            const { data: newProfile, error: insertError } = await intelliTrackClient
              .from('profiles')
              .insert({
                id: user.id,
                display_name: user.email?.split('@')[0] || 'User',
                account_type: 'client',
                market_assignments: []
              })
              .select()
              .single()
            
            if (insertError) throw insertError
            setProfile(newProfile)
          } else {
            throw error
          }
        } else {
          setProfile(data)
        }
      } catch (err) {
        setError(err as Error)
      } finally {
        setLoading(false)
      }
    }

    fetchProfile()
  }, [user])

  const updateProfile = async (updates: Partial<UserProfile>) => {
    if (!user) throw new Error('No user logged in')

    try {
      const { data, error } = await intelliTrackClient
        .from('profiles')
        .update(updates)
        .eq('id', user.id)
        .select()
        .single()

      if (error) throw error
      setProfile(data)
      return { data, error: null }
    } catch (err) {
      return { data: null, error: err as Error }
    }
  }

  return { profile, loading, error, updateProfile }
}